import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Select from 'react-select';

const typeOptions = [
  { value: 'ROLES', label: 'Roles' },
  { value: 'BP_NUMBERS', label: 'BP Numbers' },
];

import {
  AssignmentTypeSelectContainer,
  CenteredH2,
  ColorOption,
  DocumentInput,
  GeneralDocumentPickerContainer,
  ModalActions,
  ModalContent,
  ModalField,
  ModalOverlay,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import {
  cropConnectApiClient,
  GeneralDocumentDto,
  WidgetDto,
  WidgetType,
} from '../../../../services/cropConnectApiClient';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import GeneralDocumentPicker from '../../../components/GeneralDocumentPicker/GeneralDocumentPicker';

interface GeneralDocumentBannerCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  widget?: WidgetDto | null;
  parent: string;
  newOrder: number;
  onCreateSuccess: (widget: WidgetDto) => void;
  onUpdateSuccess: (widget: WidgetDto) => void;
}

interface Option {
  value: string;
  label: string;
}

const GeneralDocumentBannerCreateModal: React.FC<
  GeneralDocumentBannerCreateModalProps
> = ({
  isOpen,
  onClose,
  widget,
  parent,
  newOrder,
  onCreateSuccess,
  onUpdateSuccess,
}) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const isEditing = !!widget; // If widget exists, we're editing
  const [roles, setRoles] = useState<Option[]>([]);
  const [assignmentType, setAssignmentType] = useState<Option>({
    value: 'ROLES',
    label: 'Roles',
  });
  const [selectedRoles, setSelectedRoles] = useState<Option[]>([]);
  const [bpNumbers, setBpNumbers] = useState('');
  const [headline, setHeadline] = useState({ id: uuidv4(), text: '' });
  const [subHeadline, setSubheadline] = useState({ id: uuidv4(), text: '' });
  const [generalDocumentEcmId, setGeneralDocumentEcmId] = useState<
    number | null
  >(null);
  const [parentFolder, setParentFolder] = useState<number | null>(null);
  const [generalDocumentEcmName, setGeneralDocumentEcmName] = useState<
    string | null
  >(null);
  const [color, setColor] = useState('#CBE8C5'); // Default Green
  const [generalDocuments, setGeneralDocuments] = useState<
    GeneralDocumentDto[]
  >([]);
  const [isHeadlineInvalid, setIsHeadlineInvalid] = useState(false);
  const [isGeneralDocumentInvalid, setIsGeneralDocumentInvalid] =
    useState(false);

  const resetState = () => {
    console.log(document);
    setHeadline({ id: uuidv4(), text: '' });
    setSubheadline({ id: uuidv4(), text: '' });
    setGeneralDocumentEcmId(null);
    setParentFolder(null);
    setGeneralDocumentEcmName(null);
    setAssignmentType({ value: 'ROLES', label: 'Roles' });
    setSelectedRoles([]);
    setBpNumbers('');
    setColor('#CBE8C5');
    setIsHeadlineInvalid(false);
    setIsGeneralDocumentInvalid(false);
  };
  const handleSave = async () => {
    let isValid = true;
    if (!headline.text) {
      setIsHeadlineInvalid(true);
      isValid = false;
    }
    if (!generalDocumentEcmId || !parentFolder) {
      setIsGeneralDocumentInvalid(true);
      isValid = false;
    }
    if (!isValid) {
      return;
    }
    setLoading(true); // Show loading indicator

    if (isEditing) {
      const headlineFromStore = widget.propertyBag?.headlines.find(
        (h) => h.type === 'HEADLINE'
      );
      const subHeadlineFromStore = widget.propertyBag?.headlines.find(
        (h) => h.type === 'SUB_HEADLINE'
      );
      const updatedWidget = await cropConnectApiClient.updateWidget(
        parent,
        widget.accessId!,
        {
          widgetType: WidgetType.GENERAL_DOCUMENT_BANNER,
          order: widget.order,
          propertyBag: {
            headlines: [
              {
                id: headlineFromStore?.id || uuidv4(),
                text: headline.text,
                type: 'HEADLINE',
              },
              {
                id: subHeadlineFromStore?.id || uuidv4(),
                text: subHeadline.text,
                type: 'SUB_HEADLINE',
              },
            ],
            color,
          },
        }
      );
      if (updatedWidget.success) {
        onUpdateSuccess(updatedWidget.data!);
      } else {
        setLoading(false);
        return;
      }

      await cropConnectApiClient.updateGeneralDocument(
        widget.accessId!,
        generalDocuments[0].accessId!,
        {
          parentFolderId: parentFolder,
          documentId: generalDocumentEcmId,
          visibleFor:
            assignmentType.value === 'ROLES'
              ? selectedRoles.map((role) => role.value)
              : bpNumbers.split('\n'),
          properties: {
            name: headline.text,
            assignmentType: assignmentType.value,
            generalDocumentEcmName: generalDocumentEcmName,
            parentFolderId: parentFolder,
          },
        }
      );
    } else {
      // Create new banner
      const createdWidget = await cropConnectApiClient.addWidget(parent, {
        widgetType: WidgetType.GENERAL_DOCUMENT_BANNER,
        order: newOrder,
        propertyBag: {
          headlines: [
            { id: headline.id, text: headline.text, type: 'HEADLINE' },
            {
              id: subHeadline.id,
              text: subHeadline.text,
              type: 'SUB_HEADLINE',
            },
          ],
          color,
        },
      });
      if (createdWidget.success) {
        onCreateSuccess(createdWidget.data!);
      }
      if (!createdWidget.success) {
        setLoading(false);
        return;
      }
      if (!generalDocumentEcmId || !parentFolder) {
        setLoading(false);
        return;
      }
      await cropConnectApiClient.addGeneralDocument(
        createdWidget.data?.accessId ?? '',
        {
          visibleFor:
            assignmentType.value === 'ROLES'
              ? selectedRoles.map((role) => role.value)
              : bpNumbers.split('\n'),
          properties: {
            name: headline.text,
            assignmentType: assignmentType.value,
            generalDocumentEcmName: generalDocumentEcmName,
            parentFolderId: parentFolder,
          },
          parentHeadline: createdWidget.data?.propertyBag?.headlines.find(
            (h) => h.type === 'HEADLINE'
          )?.id,
          parentFolderId: parentFolder,
          documentId: generalDocumentEcmId,
        }
      );
    }
    setLoading(false); // Hide loading indicator
    resetState();
    onClose();
    window.location.reload();
  };

  const handleOnCancel = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (isEditing) {
      const headlineFromStore = widget.propertyBag?.headlines.find(
        (h) => h.type === 'HEADLINE'
      );
      setHeadline({ id: headlineFromStore?.id, text: headlineFromStore?.text });
      const subHeadlineFromStore = widget.propertyBag?.headlines.find(
        (h) => h.type === 'SUB_HEADLINE'
      );
      setSubheadline({
        id: subHeadlineFromStore?.id,
        text: subHeadlineFromStore?.text,
      });
      setColor(widget.propertyBag?.color || '#CBE8C5');
      const getGeneralDocuments = async () => {
        if (widget?.accessId === undefined) return;
        const generalDocuments =
          await cropConnectApiClient.getAllGeneralDocumentsForParent(
            widget.accessId
          );
        setAssignmentType(
          (generalDocuments.data?.[0] as GeneralDocumentDto)?.propertyBag
            ?.assignmentType === 'ROLES'
            ? typeOptions[0]
            : typeOptions[1]
        );
        setSelectedRoles(
          (generalDocuments.data?.[0] as GeneralDocumentDto)?.propertyBag
            ?.assignmentType === 'ROLES'
            ? (
                generalDocuments.data?.[0] as GeneralDocumentDto
              )?.visibleFor?.map((role) => ({
                value: role,
                label: role,
              })) || []
            : []
        );
        setBpNumbers(
          (generalDocuments.data?.[0] as GeneralDocumentDto)?.propertyBag
            ?.assignmentType === 'BP_NUMBERS'
            ? (
                generalDocuments.data?.[0] as GeneralDocumentDto
              )?.visibleFor?.join('\n') || ''
            : ''
        );

        setGeneralDocuments(generalDocuments.data!);
        setGeneralDocumentEcmId(
          (generalDocuments.data?.[0] as GeneralDocumentDto)?.ecmDocumentId
        );
        setParentFolder(
          (generalDocuments.data?.[0] as GeneralDocumentDto)?.propertyBag
            ?.parentFolderId
        );
        setGeneralDocumentEcmName(
          (generalDocuments.data?.[0] as GeneralDocumentDto)?.propertyBag
            ?.generalDocumentEcmName
        );
      };
      getGeneralDocuments();
    }
  }, [widget, isEditing]);

  useEffect(() => {
    const fetchRoles = async () => {
      const roles =
        await cropConnectApiClient.getGeneralDocumentsApplicableRoles();
      if (roles.data) {
        setRoles(roles.data.map((role) => ({ value: role, label: role })));
      }
    };
    if (isOpen) {
      fetchRoles();
    }
  }, [widget, isOpen]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <ModalOverlay>
        <ModalContent>
          <CenteredH2>{t(TranslationKeys.general_document_loading)}</CenteredH2>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <style>
        {`
          .error-border {
            border: 1px solid red;
          }
        `}
      </style>
      <ModalContent>
        <CenteredH2>
          {t(TranslationKeys.create_general_document_banner_title)}
        </CenteredH2>
        <ModalField>
          <label>
            {t(TranslationKeys.create_general_document_banner_headline)}
          </label>
          <input
            type="text"
            value={headline.text}
            onChange={(e) => {
              setHeadline({ ...headline, text: e.target.value });
              setIsHeadlineInvalid(false);
            }}
            style={{ borderColor: isHeadlineInvalid ? 'red' : undefined }}
          />
        </ModalField>
        <ModalField>
          <label>
            {t(TranslationKeys.create_general_document_banner_text)}
          </label>
          <input
            type="text"
            value={subHeadline.text}
            onChange={(e) =>
              setSubheadline({ ...subHeadline, text: e.target.value })
            }
          />
        </ModalField>
        <DocumentInput>
          <GeneralDocumentPickerContainer
            className={isGeneralDocumentInvalid ? 'error-border' : ''}
          >
            <div>
              {generalDocumentEcmName
                ? generalDocumentEcmName
                : t(
                    TranslationKeys.create_general_document_modal_choose_document
                  )}
            </div>
            <GeneralDocumentPicker
              onGeneralDocumentPicked={(
                generalDocumentEcmId,
                parentFolderId,
                generalDocumentEcmName
              ) => {
                setGeneralDocumentEcmId(generalDocumentEcmId);
                setParentFolder(parentFolderId);
                setGeneralDocumentEcmName(generalDocumentEcmName);
                setIsGeneralDocumentInvalid(false);
              }}
            />
          </GeneralDocumentPickerContainer>
          <AssignmentTypeSelectContainer>
            <Select
              options={typeOptions}
              value={assignmentType}
              onChange={(selected) => setAssignmentType(selected)}
              classNamePrefix="select"
              placeholder="Select an option"
            />
          </AssignmentTypeSelectContainer>
          {assignmentType.value === 'ROLES' && (
            <ModalField>
              <Select
                options={roles}
                isMulti
                value={selectedRoles}
                onChange={(selected) => setSelectedRoles(selected)}
                className="basic-multi-select"
                classNamePrefix="select"
              />
            </ModalField>
          )}
          {assignmentType.value === 'BP_NUMBERS' && (
            <ModalField>
              <textarea
                value={bpNumbers}
                onChange={(event) => setBpNumbers(event.target.value)}
                placeholder="Enter BP Numbers (one BP number per line)"
              />
            </ModalField>
          )}
        </DocumentInput>

        <ModalField>
          <label>
            {t(TranslationKeys.create_general_document_banner_color)}
          </label>
          <div>
            {['#CBE8C5', '#E3E7EB'].map((colorValue) => (
              <ColorOption
                key={colorValue}
                color={colorValue}
                selected={color === colorValue}
                onClick={() => {
                  setColor(colorValue);
                }}
                aria-label={`Select color ${colorValue}`}
              />
            ))}
          </div>
        </ModalField>
        <ModalActions>
          <ActionButton
            variant="close"
            buttonText={t(TranslationKeys.general_document_cancel_button)}
            onClickAction={handleOnCancel}
          />
          <ActionButton
            variant="accept-blue"
            buttonText={
              isEditing
                ? t(TranslationKeys.general_document_save_button)
                : t(TranslationKeys.general_document_create_button)
            }
            onClickAction={handleSave}
          />
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default GeneralDocumentBannerCreateModal;
